import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
  Typography,
  Modal,
  Box,
  Snackbar,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { WarningRounded, SearchRounded } from "@mui/icons-material";

import "./styles.scss";
import { DeleteRounded } from "@mui/icons-material";

import useModalZIndex from "@hooks/useModalZIndex";
import { Button, Input } from "@fragments";
import { Text } from "@fragments";

// Isolated so keystrokes only re-render the input itself — the modal body
// (table, list, etc.) is unaffected. Commits on Enter, blur, or button click.
function SearchField({ placeholder, onSearch }) {
  const [value, setValue] = useState("");
  const lastEmittedRef = useRef("");

  function commit() {
    const next = value;
    if (lastEmittedRef.current === next) return;
    lastEmittedRef.current = next;
    if (onSearch) onSearch(next);
  }

  function handleKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
    }
  }

  return (
    <>
      <Input.Text
        className="custom-modal-search-input"
        start={<SearchRounded />}
        value={value}
        onInput={setValue}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        placeholder={placeholder}
        autoFocus
      />
      <Button
        className="custom-modal-search-action"
        color="inverse"
        size="medium"
        icon={<SearchRounded />}
        onClick={commit}
        preventLoading
      />
    </>
  );
}

function ModalAlert({ errorTitle, errorMessage }) {
  const { t } = useTranslation("community");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (errorTitle || errorMessage) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [errorTitle, errorMessage]);

  if (!visible || (!errorTitle && !errorMessage)) return null;

  return (
    <div className="modal-alert error">
      <div className="alert-header">
        <Text.span
          weight={400}
          grow
          left={<WarningRounded />}
        >
          {errorTitle || t("modal.errorTitle")}
        </Text.span>
      </div>

      {errorMessage && (
        <>
          <div className="alert-footer">
            <Text.span>
              {errorMessage}
            </Text.span>
          </div>
        </>
      )}
    </div>
  );
}

export default function CustomModal({
  maxWidth = 1027,
  bottomButtonSumbitText,
  bottomButtonSumbitIcon,
  bottomButtonCancelText,
  handleSecondary,
  hideSecondaryButton = false,
  loading,
  disabled = false,
  snackbar = {},
  title,
  topActions = null,
  customTitle,
  handleSubmit,
  open = false,
  handleClose,
  width = "100%",
  bodyWrapperStyle = {},
  bodyComponent,
  mainButtonColor = "primary",
  bottomButtonsOrientation = "horizontal",
  className = "",
  handleDelete,
  deletionTitle,
  errorTitle,
  errorMessage,
  variant = "default",
  subHeader = null,
  onSearch,
  searchPlaceholder,
}) {
  const { t } = useTranslation("community");

  const isSearch = variant === "search";
  const [deletionOpen, setDeletionOpen] = React.useState(false);
  const { zIndex, isTop } = useModalZIndex(open);

  function deletionHandler() {
    window.openConfirm({
      subtitle: deletionTitle,
      buttons: [
        {
          text: t("modal.deleteForever"),
          onClick: handleDelete,
          variant: "contained",
          className: "",
          color: "error",
        },
        {
          text: t("modal.keepResource"),
          variant: "text",
        },
      ],
    });
  }

  const headerNode = isSearch ? (
    <div className="custom-modal-search-header">
      <div className="custom-modal-search-row">
        <SearchField placeholder={searchPlaceholder || t("modal.searchPlaceholder")} onSearch={onSearch} />
        {handleClose && (
          <IconButton
            onClick={handleClose}
            children={<CloseIcon />}
            className="custom-modal-search-close"
          />
        )}
      </div>
      {subHeader && (
        <div className="custom-modal-sub-header">{subHeader}</div>
      )}
      {loading && <LinearProgress indeterminate className="loader-modal" />}
    </div>
  ) : (
    <div className={"custom-modal-header"}>
      {customTitle ? (
        customTitle
      ) : (
        <Typography
          style={{ flexGrow: 1 }}
          component="h3"
          className="bold my-1"
        >
          {title}
        </Typography>
      )}
      {topActions}
      {handleDelete && (
        <Button
          onClick={deletionHandler}
          color="error"
          startIcon={<DeleteRounded color="error" fontSize="small" />}
          className="mr-2"
        >
          {t("modal.delete")}
        </Button>
      )}
      {handleClose && (
        <IconButton
          onClick={handleClose}
          children={<CloseIcon />}
          className="modal-close-button"
        />
      )}

      {loading && <LinearProgress indeterminate className="loader-modal" />}
    </div>
  );

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        // Drives the `.MuiModal-root` z-index (App.css) so nested modals stack.
        sx={{ "--modal-z": zIndex }}
        // Drop the focus trap while another modal sits on top, otherwise MUI
        // yanks focus back here and the modal above can't receive input.
        disableEnforceFocus={!isTop}
      >
        <Box
          sx={{ maxWidth: maxWidth, width: width }}
          className={"custom-modal " + (isSearch ? "custom-modal--search " : "") + className}
        >
          {headerNode}

          <div className={"custom-modal-body" + (isSearch ? " custom-modal-body--search" : "")}>
            <div
              className={"custom-modal-content" + (isSearch ? "" : " pb-5")}
              style={bodyWrapperStyle}
            >
              {bodyComponent}
            </div>
          </div>

          <Snackbar
            className="modal-snackbar"
            open={!!snackbar?.value}
            autoHideDuration={6000}
            onClose={snackbar?.onClose}
            message={snackbar?.value}
          />

          <ModalAlert errorTitle={errorTitle} errorMessage={errorMessage} />

          {!isSearch && (!hideSecondaryButton || !!bottomButtonSumbitText) && (
            <div
              className="buttons-bottom"
              style={{
                flexDirection:
                  bottomButtonsOrientation === "vertical" ? "column" : "row",
                gap: 8,
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              {!hideSecondaryButton && (
                <Button
                  size="large"
                  color="overlay"
                  onClick={handleSecondary || handleClose}
                  disabled={loading}
                >
                  {bottomButtonCancelText || t("common:actions.cancel")}
                </Button>
              )}
              {!!bottomButtonSumbitText && (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || disabled}
                  size="large"
                  color={mainButtonColor}
                  children={
                    bottomButtonSumbitText
                  }
                  startIcon={!loading && bottomButtonSumbitIcon}
                  variant="contained"
                />
              )}
            </div>
          )}
        </Box>
      </Modal>
      <DeletionDialog
        open={deletionOpen}
        setOpen={setDeletionOpen}
        title={deletionTitle}
        handler={handleDelete}
      />
    </div>
  );
}

function DeletionDialog({ title, handler, open, setOpen }) {
  const { t } = useTranslation("community");
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    setLoading(true);

    await handler();

    setLoading(false);
    setOpen(false);
  };

  return (
    <div>
      <Dialog open={open} onClose={handleClose} className="deletion-modal-area">
        <DialogContent>
          <h2>{t("modal.attention")}</h2>

          <span>{title}</span>
        </DialogContent>
        <DialogActions className="px-3 pb-2">
          <Button
            style={{
              color: "black",
            }}
            onClick={handleClose}
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            className="confirm-deletion py-1 px-2"
            color="error"
            variant="contained"
            loading={loading}
            disabled={loading}
            onClick={handleSubmit}
            autoFocus
          >
            {t("modal.deleteForever")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
